import { z } from "zod";
import { providerConnectionViewSchema } from "./providers.js";
export const appProviderConnectionsSchema = z.object({ connections: z.array(providerConnectionViewSchema) }).strict();
export const transferAppProviderSchema = z.object({ requestId: z.uuid(), providerConnectionId: z.uuid() }).strict();
export const providerDelegationRequestSchema = z.object({ requestId: z.uuid(), threadId: z.uuid(), providerConnectionId: z.uuid() }).strict();
export const providerDelegationResponseSchema = z.object({ delegationId: z.uuid(), revokedAt: z.string().nullable() }).strict();
export const scheduledTaskAuthoritySchema = z.object({ kind: z.literal("scheduled_task"), issuerUserId: z.uuid(),
    appId: z.uuid(), taskName: z.string().min(1).max(63), version: z.uuid(), delegationId: z.uuid() }).strict();
export const providerDelegationAuthoritySchema = z.object({ delegationId: z.uuid(), grantorUserId: z.uuid(),
    resourceKind: z.literal("agent_provider_connection"), resourceId: z.uuid(), appId: z.uuid() }).strict();
export const incidentRepairAuthoritySchema = z.object({ kind: z.literal("incident_repair"), issuerUserId: z.uuid(), appId: z.uuid(), delegationId: z.uuid() }).strict();
const id = z.uuid();
export const agentOrganisationSchema = z.object({
    id, name: z.string(), canCreateApp: z.boolean(),
}).strict();
export const agentAccountOrganisationSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("personal"), organisation: z.null() }).strict(),
    z.object({ state: z.literal("onboarding_required"), organisation: z.null() }).strict(),
    z.object({ state: z.literal("active"), directory: agentOrganisationSchema }).strict(),
    z.object({ state: z.enum(["removed", "suspended"]), organisation: z.object({ id, name: z.string() }).strict() }).strict(),
]);
export const agentDirectorySchema = z.object({ account: agentAccountOrganisationSchema,
    organisations: z.array(agentOrganisationSchema).max(1) }).strict();
export const conversationSubjectSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("standalone"), conversationId: id, organisationId: id, authorUserId: id }).strict(),
    z.object({ kind: z.literal("app"), conversationId: id, organisationId: id, appId: id }).strict(),
]);
/** Logical identity is separate from the legacy runtime's appId storage key. */
export const conversationExecutionSchema = z.object({
    version: z.literal(1),
    subject: conversationSubjectSchema,
    partitionId: id,
    actorUserId: id,
    activityActor: z.union([z.object({ kind: z.literal("user"), userId: id }).strict(), z.object({ kind: z.literal("system") }).strict()]).optional(),
    taskAuthority: scheduledTaskAuthoritySchema.optional(),
    repairAuthority: incidentRepairAuthoritySchema.optional(),
}).strict();
export const conversationPayerSchema = z.object({
    organisationId: id,
    organisationName: z.string().optional(),
});
const exactQuantity = z.string().regex(/^(0|[1-9][0-9]*)$/);
export const conversationRunPayerSchema = z.object({
    admissionId: id,
    state: z.enum(["reserved", "unknown", "settled", "released", "denied"]),
    payer: z.object({ organisationId: id, workspaceId: id.nullable().optional(), placementVersion: z.string().nullable() }),
    period: z.string(),
    maximum: z.object({ agent_tokens: exactQuantity, agent_concurrency: exactQuantity, ai_cost: exactQuantity }).strict(),
    denialCode: z.string().nullable(),
});
export const createStandaloneConversationSchema = z.object({
    requestId: id,
    organisationId: id.optional(),
    title: z.string().trim().min(1).max(500).default("New chat"),
    fundingSource: z.enum(["user", "platform"]).default("platform"),
    providerConnectionId: id.nullable().optional(),
    harnessVersion: z.enum(["opencloud-codex-ai-sdk-harness-v3", "opencloud-claude-code-harness-v1", "opencloud-opencode2-harness-v1",
        "opencloud-codex-openrouter-harness-v1", "opencloud-opencode2-openrouter-harness-v1"]).optional(),
}).strict();
export const promoteConversationSchema = z.object({
    requestId: id,
    workspaceId: id.optional(),
    name: z.string().trim().min(1).max(120),
    brief: z.string().trim().min(1).max(16_000),
    fundingSource: z.enum(["platform", "user"]).default("platform"),
    providerConnectionId: id.optional(),
    delegateProvider: z.boolean().optional(),
}).strict().refine(value => value.fundingSource !== "user" || (value.providerConnectionId && value.delegateProvider === true), { message: "Choose a personal provider and explicitly allow the new app to use it" });
export const conversationRecordSchema = z.object({
    id,
    kind: z.enum(["standalone", "app"]),
    authorUserId: id,
    organisationId: id,
    appId: id.nullable(),
    title: z.string(),
    payer: conversationPayerSchema.nullable(),
    canSend: z.boolean(),
    canCancel: z.boolean(),
    pinned: z.boolean().default(false),
    unread: z.boolean().default(false),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
}).strict();
export const conversationSidebarUpdateSchema = z.object({
    title: z.string().trim().min(1).max(80).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
    unread: z.boolean().optional(),
}).strict().refine(value => Object.values(value).filter(item => item !== undefined).length === 1, "Change exactly one conversation sidebar setting");
//# sourceMappingURL=conversations.js.map